import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/api/client";
import { Avatar } from "@/components/Avatar";
import { Skeleton } from "@/components/Skeleton";
import { presenceLabel } from "@/lib/presence";
import type { FeedActor } from "@/lib/auditKinds";
import { roleLabel } from "@/types";

interface PersonRow {
  user: FeedActor;
  email: string;
  status: "active" | "away" | "offline";
  last_active_at: string | null;
  last_seen_at: string | null;
  active_minutes: number;
  published: number;
  removed: number;
  removal_ratio: number;
  jobs_done: number;
  jobs_error: number;
  jobs_cancelled: number;
  deals_created: number;
  deals_done: number;
  deals_lost: number;
  clients_created: number;
  shares_created: number;
  share_views: number;
}

type SortKey =
  | "presence" | "published" | "removed" | "active_minutes" | "deals" | "shares" | "views" | "failed";

const COLUMNS: { key: SortKey; label: string; hint: string }[] = [
  { key: "active_minutes", label: "Active", hint: "Time actually interacting, from presence history" },
  { key: "published", label: "Published", hint: "Listing rows created (one source on two portals counts twice)" },
  { key: "removed", label: "Removed", hint: "Placements taken down" },
  { key: "failed", label: "Failed", hint: "Publish jobs that errored or were cancelled" },
  { key: "deals", label: "Deals", hint: "Won / lost in this window" },
  { key: "shares", label: "Shares", hint: "Share links sent to clients" },
  { key: "views", label: "Views", hint: "Times clients opened those links" },
];

function dur(min: number): string {
  if (!min) return "—";
  const h = Math.floor(min / 60);
  return h ? `${h}h ${min % 60}m` : `${min}m`;
}

const DOT: Record<string, string> = {
  active: "bg-emerald-400",
  away: "bg-amber-400",
  offline: "bg-slate-600",
};

// Higher sorts first, so presence is scored rather than ranked — active (2)
// above away (1) above offline (0). Within a band, whoever interacted most
// recently comes first.
const PRESENCE_SCORE: Record<string, number> = { active: 2, away: 1, offline: 0 };

function recency(r: PersonRow): number {
  const t = r.last_active_at ?? r.last_seen_at;
  return t ? new Date(t).getTime() : 0;
}

function val(r: PersonRow, k: SortKey): number {
  switch (k) {
    case "presence": return PRESENCE_SCORE[r.status] ?? 0;
    case "deals": return r.deals_done + r.deals_lost;
    case "shares": return r.shares_created;
    case "views": return r.share_views;
    case "failed": return r.jobs_error + r.jobs_cancelled;
    default: return r[k];
  }
}

function compare(a: PersonRow, b: PersonRow, k: SortKey): number {
  const d = val(b, k) - val(a, k);
  if (d !== 0) return d;
  // Presence ties break on recency; every other column breaks on presence, so
  // two people with the same output are ordered by who is actually around.
  if (k === "presence") return recency(b) - recency(a);
  const p = val(b, "presence") - val(a, "presence");
  if (p !== 0) return p;
  return (a.user.name || "").localeCompare(b.user.name || "");
}

export function PeopleTab({ range }: { range: string }) {
  const [rows, setRows] = useState<PersonRow[] | null>(null);
  const [sort, setSort] = useState<SortKey>("published");
  const navigate = useNavigate();

  useEffect(() => {
    let alive = true;
    setRows(null);
    api<{ rows: PersonRow[] }>(`/api/audit/people?range=${range}`)
      .then((r) => alive && setRows(r.rows))
      .catch(() => alive && setRows([]));
    return () => {
      alive = false;
    };
  }, [range]);

  const sorted = useMemo(
    () => (rows ? [...rows].sort((a, b) => compare(a, b, sort)) : []),
    [rows, sort],
  );

  if (rows === null) {
    return (
      <div className="card p-5 space-y-3">
        {Array.from({ length: 8 }).map((_, i) => (
          <Skeleton key={i} className="h-9 w-full" />
        ))}
      </div>
    );
  }

  return (
    <div className="card overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="text-[10px] uppercase tracking-wider text-slate-500 border-b border-white/5">
            <tr>
              <th
                className="text-left font-medium px-4 py-2.5"
                title="Sort by who is online right now"
              >
                <button
                  type="button"
                  onClick={() => setSort("presence")}
                  className={`hover:text-slate-300 transition ${
                    sort === "presence" ? "text-accent-300" : ""
                  }`}
                >
                  Person
                  {sort === "presence" ? " ↓" : ""}
                </button>
              </th>
              {COLUMNS.map((c) => (
                <th key={c.key} className="text-right font-medium px-3 py-2.5" title={c.hint}>
                  <button
                    type="button"
                    onClick={() => setSort(c.key)}
                    className={`hover:text-slate-300 transition ${
                      sort === c.key ? "text-accent-300" : ""
                    }`}
                  >
                    {c.label}
                    {sort === c.key && " ↓"}
                  </button>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {sorted.map((r) => {
              const p = presenceLabel(r.status, r.last_active_at, r.last_seen_at);
              const failed = r.jobs_error + r.jobs_cancelled;
              return (
                <tr
                  key={r.user.id ?? r.email}
                  onClick={() => r.user.id && navigate(`/audit/users/${r.user.id}`)}
                  className="border-b border-white/5 hover:bg-white/[0.02] cursor-pointer"
                >
                  <td className="px-4 py-2.5">
                    <div className="flex items-center gap-2.5 min-w-0">
                      <Avatar name={r.user.name || "?"} size="xs" />
                      <div className="min-w-0">
                        <div className="flex items-center gap-1.5">
                          <span className={`h-1.5 w-1.5 rounded-full ${DOT[r.status]}`} />
                          <span className="text-slate-200 truncate max-w-[170px]">
                            {r.user.name}
                          </span>
                        </div>
                        <div className="text-[11px] text-slate-500 truncate">
                          {roleLabel(r.user.role)}
                          {p ? ` · ${p.text.toLowerCase()}` : ""}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="px-3 py-2.5 text-right tabular-nums text-slate-400">
                    {dur(r.active_minutes)}
                  </td>
                  <td className="px-3 py-2.5 text-right tabular-nums text-slate-100 font-medium">
                    {r.published || "—"}
                  </td>
                  <td className="px-3 py-2.5 text-right tabular-nums">
                    {/* A broker removing far more than they publish is the
                        signal worth surfacing — Omari-style bulk teardowns. */}
                    <span
                      className={
                        r.removed > 50 && r.removed > r.published
                          ? "text-rose-300 font-medium"
                          : "text-slate-400"
                      }
                      title={
                        r.published
                          ? `${r.removal_ratio.toFixed(2)}× published`
                          : undefined
                      }
                    >
                      {r.removed || "—"}
                    </span>
                  </td>
                  <td className="px-3 py-2.5 text-right tabular-nums">
                    <span className={failed ? "text-amber-300" : "text-slate-600"}>
                      {failed || "—"}
                    </span>
                  </td>
                  <td className="px-3 py-2.5 text-right tabular-nums text-slate-400">
                    {r.deals_done || r.deals_lost ? (
                      <>
                        <span className="text-emerald-300">{r.deals_done}</span>
                        <span className="text-slate-600"> / </span>
                        <span className="text-slate-500">{r.deals_lost}</span>
                      </>
                    ) : (
                      "—"
                    )}
                  </td>
                  <td className="px-3 py-2.5 text-right tabular-nums text-slate-400">
                    {r.shares_created || "—"}
                  </td>
                  <td className="px-3 py-2.5 text-right tabular-nums">
                    <span className={r.share_views ? "text-violet-300" : "text-slate-600"}>
                      {r.share_views || "—"}
                    </span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <div className="px-4 py-2.5 border-t border-white/5 text-[11px] text-slate-600">
        Deals show won / lost. Views are client opens of a share link, not
        teammate clicks.
      </div>
    </div>
  );
}
